import { Injectable } from '@angular/core';
import { OrderMenuStore } from '../stores/order-menu.store';
import { OrderMenuCartStore } from '../stores/order-menu-cart.store';
import { IOrderMenuItem, IOrderMenuCategory } from '../pages/v2-order-pay/models/order-menu.models';
import { RxUtil } from '../utility/rx.util';
import { Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import { AnalyticsService } from './analytics.service';
import { LocationStore } from 'stores/location.store';
import { TableStore } from 'stores/table.store';
import { OrderMenuService } from './order-menu.service';
import { ItemDetailsStore } from 'stores/item-details.store';
import { OrderPayStore } from 'stores/order-pay.store';

export interface MenuItemSearchResult {
  id: string;
  name: string;
  description: string;
  price: number;
  category?: string;
  image?: string;
  inStock: boolean;
  tags?: string[];
  badge?: string;
}

export interface CartSummary {
  itemCount: number;
  subtotal: number;
  items: Array<{
    name: string;
    quantity: number;
    price: number;
  }>;
}

@Injectable({
  providedIn: 'root'
})
export class ChatAssistantService {
  constructor(
    private orderMenuStore: OrderMenuStore,
    private orderMenuCartStore: OrderMenuCartStore,
    private orderMenuService: OrderMenuService,
    private itemDetailsStore: ItemDetailsStore,
    private router: Router,
    private navController: NavController,
    private orderPayStore: OrderPayStore,
    private analyticsService: AnalyticsService,
    private locationStore: LocationStore,
    private tableStore: TableStore
  ) {}

  /**
   * Wait for menu to load with a timeout
   * Polls the menu store until categories are available or timeout is reached
   */
  private async waitForMenuToLoad(maxWaitMs: number = 5000): Promise<IOrderMenuCategory[]> {
    const startTime = Date.now();
    const pollInterval = 200; // Check every 200ms
    
    while (Date.now() - startTime < maxWaitMs) {
      const categories = await RxUtil.takeOne(this.orderMenuStore.orderMenuCategories$);
      
      if (categories && categories.length > 0) {
        console.log('Menu loaded successfully after', Date.now() - startTime, 'ms');
        return categories;
      }
      
      // Wait before next check
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
    
    console.warn('Menu loading timeout after', maxWaitMs, 'ms');
    return await RxUtil.takeOne(this.orderMenuStore.orderMenuCategories$);
  }

  /**
   * Get the complete menu context for the AI assistant
   */
  async getMenuContext(): Promise<string> {
    const location = this.locationStore.currentLocation;
    let context = `Restaurant: ${location?.name || 'Unknown'}\n`;
    
    // Wait for menu to load (with timeout)
    const categories = await this.waitForMenuToLoad();
    
    console.log('Building menu context - Categories count:', categories.length);
    console.log('Location:', location?.name);
    
    if (!categories || categories.length === 0) {
      console.warn('WARNING: No menu categories available after waiting! User may not be on ordering page.');
      return context + '\nI\'m ready to help! However, I don\'t have access to the menu yet. Please navigate to the ordering page to see our menu, or ask me general questions about the restaurant.';
    }
    
    context += `Menu Categories:\n\n`;

    let itemCount = 0;
    categories.forEach(category => {
      if (!category.isDisabled) {
        context += `Category: ${category.name}\n`;
        if (category.description) {
          context += `  ${category.description}\n`;
        }
        
        category.sections.forEach(section => {
          if (!section.isDisabled && section.items.length > 0) {
            context += `  Section: ${section.title}\n`;
            if (section.description) {
              context += `    ${section.description}\n`;
            }
            
            section.items.forEach(item => {
              if (item.type === 'item' && !item.isDisabled && item.inStock) {
                itemCount++;
                // Include item ID so ChatKit can reference it correctly
                context += `    - ${item.name} [ID: ${item.id}] ($${(item.price / 100).toFixed(2)})`;
                if (item.description) {
                  context += `: ${item.description}`;
                }
                if (item.tags && item.tags.length > 0) {
                  context += ` [${item.tags.map(t => t.full).join(', ')}]`;
                }
                context += `\n`;
              }
            });
          }
        });
        context += `\n`;
      }
    });

    console.log(`Menu context built: ${categories.length} categories, ${itemCount} items, ${context.length} chars`);
    
    if (itemCount === 0) {
      console.warn('WARNING: Menu context has no items! Either menu is not loaded or all items are disabled/out of stock.');
    }

    return context;
  }

  /**
   * Search for menu items by query
   */
  async searchMenuItems(query: string): Promise<MenuItemSearchResult[]> {
    const allItems = await RxUtil.takeOne(this.orderMenuStore.orderMenuItems$);
    const categories = await RxUtil.takeOne(this.orderMenuStore.orderMenuCategories$);
    
    const searchLower = query.toLowerCase();
    const results: MenuItemSearchResult[] = [];
    
    // Build a map of item IDs to their categories
    const itemCategoryMap = new Map<string, string>();
    categories.forEach(category => {
      category.sections.forEach(section => {
        section.items.forEach(item => {
          itemCategoryMap.set(item.id, category.name);
        });
      });
    });

    allItems.forEach(item => {
      const nameMatch = item.name.toLowerCase().includes(searchLower);
      const descMatch = item.description?.toLowerCase().includes(searchLower);
      const tagMatch = item.tags?.some(tag => 
        tag.full.toLowerCase().includes(searchLower)
      );

      if (nameMatch || descMatch || tagMatch) {
        results.push({
          id: item.id,
          name: item.name,
          description: item.description || '',
          price: item.price / 100,
          category: itemCategoryMap.get(item.id),
          image: item.image?.url,
          inStock: item.inStock,
          tags: item.tags?.map(t => t.full),
          badge: item.badge?.text
        });
      }
    });

    return results.slice(0, 10); // Limit to 10 results
  }

  /**
   * Get detailed information about a specific menu item
   */
  async getItemDetails(itemId: string): Promise<MenuItemSearchResult | null> {
    console.log('getItemDetails called with itemId:', itemId);
    const allItems = await RxUtil.takeOne(this.orderMenuStore.orderMenuItems$);
    const item = allItems.find(i => i.id === itemId);

    console.log('getItemDetails found item:', item);
    if (!item) return null;

    return {
      id: item.id,
      name: item.name,
      description: item.description || '',
      price: item.price / 100,
      image: item.image?.url,
      inStock: item.inStock,
      tags: item.tags?.map(t => t.full),
      badge: item.badge?.text
    };
  }

  /**
   * Get items by category
   */
  async getItemsByCategory(categoryName: string): Promise<MenuItemSearchResult[]> {
    const categories = await RxUtil.takeOne(this.orderMenuStore.orderMenuCategories$);
    const category = categories.find(c => 
      c.name.toLowerCase().includes(categoryName.toLowerCase())
    );

    if (!category) return [];

    const results: MenuItemSearchResult[] = [];
    category.sections.forEach(section => {
      section.items.forEach(item => {
        if (item.type === 'item') {
          results.push({
            id: item.id,
            name: item.name,
            description: item.description || '',
            price: item.price / 100,
            category: category.name,
            image: item.image?.url,
            inStock: item.inStock,
            tags: item.tags?.map(t => t.full),
            badge: item.badge?.text
          });
        }
      });
    });

    return results;
  }

  /**
   * Get items by dietary tags (vegetarian, vegan, gluten-free, etc.)
   */
  async getItemsByDietaryTag(tag: string): Promise<MenuItemSearchResult[]> {
    const allItems = await RxUtil.takeOne(this.orderMenuStore.orderMenuItems$);
    const tagLower = tag.toLowerCase();

    return allItems
      .filter(item => 
        item.tags?.some(t => t.full.toLowerCase().includes(tagLower))
      )
      .map(item => ({
        id: item.id,
        name: item.name,
        description: item.description || '',
        price: item.price / 100,
        image: item.image?.url,
        inStock: item.inStock,
        tags: item.tags?.map(t => t.full),
        badge: item.badge?.text
      }))
      .slice(0, 10);
  }

  /**
   * Add an item to the cart (simplified version for items without modifiers)
   */
  async addItemToCart(itemId: string, quantity: number = 1, itemName?: string): Promise<{ success: boolean; message: string; requiresModifiers?: boolean }> {
    const allItems = await RxUtil.takeOne(this.orderMenuStore.orderMenuItems$);
    let item = allItems.find(i => i.id === itemId);

    // If item not found by ID, try to find by name (fallback for ChatKit internal IDs)
    if (!item && itemId) {
      console.warn(`Item not found by ID "${itemId}", attempting to find by name fallback`);
      
      // First try exact name match if provided
      if (itemName) {
        item = allItems.find(i => 
          i.name.toLowerCase() === itemName.toLowerCase()
        );
      }
      
      // If still not found, try partial matches
      if (!item) {
        const searchTerm = (itemName || itemId).toLowerCase();
        item = allItems.find(i => 
          i.name.toLowerCase() === searchTerm ||
          i.name.toLowerCase().includes(searchTerm) ||
          i.id.toLowerCase().includes(searchTerm)
        );
      }
      
      if (item) {
        console.log(`Found item by name fallback: "${item.name}" (ID: ${item.id})`);
      }
    }

    if (!item) {
      console.error(`Item not found with ID: "${itemId}"${itemName ? ` or name: "${itemName}"` : ''}`);
      console.error('Sample available items:', 
        allItems.slice(0, 10).map(i => ({ id: i.id, name: i.name }))
      );
      return { success: false, message: `Item not found: ${itemName || itemId}` };
    }

    if (!item.inStock) {
      return { success: false, message: `${item.name} is currently out of stock` };
    }

    // If item has modifiers, we need to open the item details page
    if (item.hasModifiers) {
      this.analyticsService.log('Chat Assistant - Item requires customization');
      // Set the item ID in the store first
      this.itemDetailsStore.selectedItemId = itemId;
      // Navigate to item details page using the correct route structure
      const beaconId = this.orderPayStore.beaconId;
      if (beaconId) {
        await this.navController.navigateForward([
          '/',
          beaconId,
          'v2',
          'order',
          'item-details'
        ]);
      } else {
        console.error('Cannot navigate to item details: beaconId not available');
        return {
          success: false,
          message: 'Unable to navigate to item details. Please try again.',
          requiresModifiers: true
        };
      }
      return {
        success: false,
        message: `${item.name} has customization options. Please complete your selections.`,
        requiresModifiers: true
      };
    }

    // Add item directly to cart
    this.orderMenuCartStore.saveItemToCart(
      item,
      [],
      {
        quantity,
        comment: ''
      }
    );

    this.analyticsService.log('Chat Assistant - Item added to cart');

    return {
      success: true,
      message: `Added ${quantity} ${item.name}${quantity > 1 ? 's' : ''} to your cart`
    };
  }

  /**
   * Remove an item from the cart
   */
  async removeFromCart(itemId?: string, itemName?: string): Promise<{ success: boolean; message: string }> {
    const cartEntries = await RxUtil.takeOne(this.orderMenuCartStore.cartEntries$);
    
    if (cartEntries.length === 0) {
      return { success: false, message: 'Your cart is empty.' };
    }

    // Find the cart entry by item ID or name
    let entryToRemove = cartEntries.find(entry => {
      if (itemId && entry.item.id === itemId) return true;
      if (itemName && entry.item.name.toLowerCase() === itemName.toLowerCase()) return true;
      if (itemName && entry.item.name.toLowerCase().includes(itemName.toLowerCase())) return true;
      return false;
    });

    if (!entryToRemove) {
      // If no specific item found, list what's in the cart
      const itemNames = cartEntries.map(e => e.item.name).join(', ');
      return { 
        success: false, 
        message: `Item not found in cart. Your cart contains: ${itemNames}` 
      };
    }

    // Remove the entry from cart
    this.orderMenuCartStore.removeEntryFromCart(entryToRemove);
    this.analyticsService.log('Chat Assistant - Item removed from cart');

    return {
      success: true,
      message: `Removed ${entryToRemove.item.name} from your cart`
    };
  }

  /**
   * Clear all items from the cart
   */
  async clearCart(): Promise<{ success: boolean; message: string }> {
    const cartEntries = await RxUtil.takeOne(this.orderMenuCartStore.cartEntries$);
    
    if (cartEntries.length === 0) {
      return { success: false, message: 'Your cart is already empty.' };
    }

    this.orderMenuCartStore.clearCart();
    this.analyticsService.log('Chat Assistant - Cart cleared');

    return {
      success: true,
      message: 'Your cart has been cleared'
    };
  }

  /**
   * Get current cart summary
   */
  async getCartSummary(): Promise<CartSummary> {
    const cartEntries = await RxUtil.takeOne(this.orderMenuCartStore.cartEntries$);
    const cartTotal = await RxUtil.takeOne(this.orderMenuCartStore.cartTotal$);

    return {
      itemCount: cartEntries.reduce((sum, entry) => sum + entry.quantity, 0),
      subtotal: cartTotal / 100,
      items: cartEntries.map(entry => ({
        name: entry.item.name,
        quantity: entry.quantity,
        price: (entry.item.price * entry.quantity) / 100
      }))
    };
  }

  /**
   * Navigate to cart/review order page
   */
  async viewCart(): Promise<void> {
    this.analyticsService.log('Chat Assistant - View cart');
    const beaconId = this.orderPayStore.beaconId;
    if (beaconId) {
      await this.navController.navigateForward([
        '/',
        beaconId,
        'v2',
        'order',
        'review'
      ]);
    } else {
      console.error('Cannot navigate to review order: beaconId not available');
    }
  }

  /**
   * Start checkout process
   */
  async beginCheckout(): Promise<{ success: boolean; message: string }> {
    const cartEntries = await RxUtil.takeOne(this.orderMenuCartStore.cartEntries$);
    
    if (cartEntries.length === 0) {
      return { success: false, message: 'Your cart is empty. Please add items before checking out.' };
    }

    this.analyticsService.log('Chat Assistant - Begin checkout');
    const beaconId = this.orderPayStore.beaconId;
    if (beaconId) {
      await this.navController.navigateForward([
        '/',
        beaconId,
        'v2',
        'order',
        'review'
      ]);
    } else {
      console.error('Cannot navigate to review order: beaconId not available');
      return { success: false, message: 'Unable to navigate to checkout. Please try again.' };
    }

    return { success: true, message: 'Navigating to checkout...' };
  }

  /**
   * Get restaurant information
   */
  getRestaurantInfo(): { name: string; table?: string } {
    return {
      name: this.locationStore.currentLocation?.name || 'Restaurant',
      table: this.tableStore.currentTable?.name
    };
  }

  /**
   * Get all available categories
   */
  async getCategories(): Promise<string[]> {
    const categories = await RxUtil.takeOne(this.orderMenuStore.orderMenuCategories$);
    return categories.map(c => c.name);
  }

  /**
   * Navigate to item details page for viewing/ordering an item
   */
  async navigateToItemDetails(itemId: string): Promise<{ success: boolean; message: string }> {
    const allItems = await RxUtil.takeOne(this.orderMenuStore.orderMenuItems$);
    const item = allItems.find(i => i.id === itemId);

    if (!item) {
      return { success: false, message: 'Item not found' };
    }

    // Set the item ID in the store first
    this.itemDetailsStore.selectedItemId = itemId;
    
    // Navigate to item details page using the correct route structure
    const beaconId = this.orderPayStore.beaconId;
    if (beaconId) {
      await this.navController.navigateForward([
        '/',
        beaconId,
        'v2',
        'order',
        'item-details'
      ]);
      return { success: true, message: 'Opening item details...' };
    } else {
      console.error('Cannot navigate to item details: beaconId not available');
      return { success: false, message: 'Unable to navigate to item details. Please try again.' };
    }
  }
}
